import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Split, Stack, useTheme, withShade} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {PuzzlePieceLine} from '@dropbox/dig-icons/assets';
import {analyticsLogger} from 'analytics/analyticsLogger';
import {AvatarIcon} from 'components/DSYS/Avatar';
import {CompanyAskGlyph} from 'components/DSYS/CompanyOSLogo';
import {Title} from 'components/DSYS/Title';
import {ClientInstallSteps} from 'components/mcp/ClientInstallSteps';
import {t} from 'i18next';
import {ComponentProps, ReactNode, useCallback} from 'react';
import {useSearchParams} from 'react-router';

import {McpBuiltInGuide} from './McpBuiltInGuide';
import {type Capability, CLIENT_GUIDES, resolveCapabilities} from './mcpClientGuides';
import styles from './McpSetup.module.css';

// Query param used to deep-link a specific install guide, e.g. settings?t=mcp&client=cursor.
export const MCP_CLIENT_PARAM = 'client';
const CLIENT_PARAM = MCP_CLIENT_PARAM;
const DEFAULT_CLIENT_ID = 'CompanyOS';

const Body = ({children, ...props}: ComponentProps<typeof Text>) => (
  <Box>
    <Text {...props}>{children}</Text>
  </Box>
);

const Section = ({title, children}: {title: string; children: ReactNode}) => (
  <Box
    padding="24"
    borderRadius="Medium"
    borderStyle="Solid"
    borderColor="Border Subtle"
    backgroundColor="Background Base"
  >
    <Stack gap="16">
      <Title size={20}>{title}</Title>
      {children}
    </Stack>
  </Box>
);

const CapabilityGrid = ({capabilities}: {capabilities: Capability[]}) => (
  <div className={styles.grid}>
    {capabilities.map((cap) => (
      <div key={cap.group} className={styles.toolCard}>
        <Split gap="12" alignY="top">
          <Split.Item>
            <AvatarIcon src={cap.icon} />
          </Split.Item>
          <Split.Item width="fill">
            <Stack gap="2">
              <Body isBold>{cap.group}</Body>
              <Body size="small" color="subtle">
                {cap.description}
              </Body>
            </Stack>
          </Split.Item>
        </Split>
      </div>
    ))}
  </div>
);

export const McpSetupContent = () => {
  const {mode} = useTheme();
  const isDark = mode === 'dark';
  const [searchParams, setSearchParams] = useSearchParams();

  // Derive the active client from the URL so links like settings?t=mcp&client=cursor
  // open the matching guide; fall back to the default when the param is
  // missing or points at an unknown client.
  const requestedClient = searchParams.get(CLIENT_PARAM);
  const activeGuide =
    CLIENT_GUIDES.find((guide) => guide.id === requestedClient) ?? CLIENT_GUIDES[0];
  const client = activeGuide.id;

  const selectClient = useCallback(
    (nextClient: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (nextClient === DEFAULT_CLIENT_ID) {
            next.delete(CLIENT_PARAM);
          } else {
            next.set(CLIENT_PARAM, nextClient);
          }
          return next;
        },
        {replace: true}
      );
      analyticsLogger().logEvent('MCP_SETUP_CLIENT_SELECTED', {client: nextClient});
    },
    [setSearchParams]
  );

  return (
    <Stack gap="24">
      <Box className={styles.hero} padding="32" marginTop="16">
        <div className={styles.heroGlow} />
        <Stack gap="8">
          <Title
            size={28}
            withAccessoryStart={
              <Box style={{marginInline: -8}} marginTop="8" paddingRight="8">
                <UIIcon src={CompanyAskGlyph} size="large" />
              </Box>
            }
          >
            {t('mcp_setup_hero_title')}
          </Title>
          <Box style={{maxWidth: 640}}>
            <Text size="large" color="subtle">
              {t('mcp_setup_hero_subtitle_primary')}
            </Text>
          </Box>
        </Stack>
      </Box>
      <Section title={t('mcp_setup_section_tools')}>
        <CapabilityGrid capabilities={resolveCapabilities()} />
      </Section>

      <Section title={t('mcp_setup_section_installation')}>
        <div className={styles.setupLayout}>
          <Stack gap="2">
            {CLIENT_GUIDES.map((guide) => {
              const selected = guide.id === client;
              return (
                <button
                  key={guide.id}
                  type="button"
                  onClick={() => selectClient(guide.id)}
                  aria-pressed={selected}
                  {...withShade({
                    className: `${atoms({
                      display: 'flex',
                      alignItems: 'center',
                      paddingX: '12',
                      paddingY: '8',
                      borderRadius: 'Medium',
                      cursor: 'pointer',
                      width: '100%',
                      backgroundColor: 'transparent',
                      borderWidth: '0',
                    })}
                      `,
                    style: {marginInline: -12, gap: 10},
                    state: selected ? 'active' : undefined,
                  })}
                >
                  {guide.logo ? (
                    <img
                      src={isDark ? guide.logo.dark : guide.logo.light}
                      alt=""
                      className={styles.railLogo}
                    />
                  ) : (
                    <span className={styles.railLogo}>
                      <UIIcon src={guide.icon ?? PuzzlePieceLine} />
                    </span>
                  )}
                  <Text isBold>{guide.name}</Text>
                </button>
              );
            })}
          </Stack>

          <Box padding="20" borderRadius="Medium" backgroundColor="Background Subtle">
            {activeGuide.builtIn ? <McpBuiltInGuide /> : <ClientInstallSteps guide={activeGuide} />}
          </Box>
        </div>
      </Section>
    </Stack>
  );
};
